import { Observable, merge, defer, combineLatest, BehaviorSubject } from 'rxjs'
import { CursorPaged, CursorPageQuery, EncodedCursor } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'
import {
  startWith,
  debounceTime,
  distinctUntilChanged,
  share,
  withLatestFrom,
  switchMap,
  map,
  filter,
  tap,
} from 'rxjs/operators'
import { withCancellation, teardown } from 'utils/rx-util'

/**
 * Item source.
 */
export type ItemSource<V> = (
  searchText: string,
  params: CursorPageQuery,
  ct: CancellationToken,
) => Promise<CursorPaged<V>>

/**
 * Item sources.
 */
export const ItemSource = {
  /**
   * Static item source.
   *
   * @param items
   * @param matches
   */
  static<V>(
    items: V[],
    matches = (item: V, text: string) =>
      `${item}`.toLowerCase().includes(text.toLowerCase()),
  ): ItemSource<V> {
    return async (searchText) => {
      const result = items.filter((v) => matches(v, searchText))
      return {
        cursor: null,
        data: result,
      }
    }
  },
}

/**
 * Observable for managing the custom select search state.
 *
 * @param input$
 * @param onLoadMore$
 * @param itemSource
 * @param debounce
 */
export function CustomSelectStateObservable<V>(
  input$: Observable<string>,
  onLoadMore$: Observable<unknown>,
  itemSource: ItemSource<V>,
  debounce: number,
): Observable<CustomSelectEvent<V>> {
  return defer(() => {
    // The cursor to use for paging.
    const currentCursor = new BehaviorSubject<EncodedCursor>(null)

    // New searches.
    const newSearches$ = input$.pipe(
      startWith(''),
      debounceTime(debounce),
      distinctUntilChanged(),
      // Reset the cursor on new searches.
      tap(() => currentCursor.next(null)),
      share(),
    )

    // "Next page" requests.
    // We want to stop loading more once we've reached the
    // end. A new search would return us a new non-null cursor,
    // which will resume this observable.
    const next$ = onLoadMore$.pipe(
      withLatestFrom(currentCursor),
      map(([, cursor]) => cursor),
      // Whenever a load request comes in, if
      // we don't have a cursor, do nothing.
      filter((cursor) => cursor !== null),
      share(),
    )

    // Search start
    const searchRequests$ = combineLatest([
      newSearches$,
      next$.pipe(
        // Need to emit an initial value to kick off `combineLatest`.
        startWith(null),
      ),
    ]).pipe(
      // We want the latest cursor.
      withLatestFrom(currentCursor),
      // Map it to something nicer.
      map(([[searchText], after]) => ({ searchText, after })),
      // Prevent multiple consecutive loads for the same cursor and search text.
      distinctUntilChanged(
        (left, right) =>
          left.searchText + left.after === right.searchText + right.after,
      ),
      share(),
    )

    // Search results.
    const searchResults$ = searchRequests$.pipe(
      // Do the actual search.
      switchMap(({ searchText, after }) =>
        withCancellation((ct) => itemSource(searchText, { after }, ct)),
      ),
      // Update the cursor when we get a result.
      tap((r) => currentCursor.next(r.cursor)),
      // Make sure there's only a single search observable.
      share(),
    )

    // Map observables to events.
    const reset$ = newSearches$.pipe(
      map(() => ({ type: 'ResetItems' }) as ResetItems),
    )
    const loading$ = searchRequests$.pipe(
      map(() => ({ type: 'Loading' }) as Loading),
    )
    const loaded$ = searchResults$.pipe(
      map((page) => ({ type: 'Loaded', items: page.data }) as Loaded<V>),
    )
    const eos$ = searchResults$.pipe(
      filter((v) => v.cursor === null),
      map(() => ({ type: 'EndOfStream' }) as EndOfStream),
    )

    return merge(reset$, loading$, loaded$, eos$).pipe(
      teardown(() => currentCursor.complete()),
      share(),
    )
  })
}

/**
 * Event types.
 */
export type CustomSelectEvent<V> =
  | ResetItems
  | Loading
  | Loaded<V>
  | EndOfStream

/**
 * The items have been reset, a new search is starting.
 */
export interface ResetItems {
  type: 'ResetItems'
}

/**
 * Started loading.
 */
export interface Loading {
  type: 'Loading'
}

/**
 * Finished loading, contains all relevant items, including past ones.
 */
export interface Loaded<V> {
  type: 'Loaded'
  items: V[]
}

/**
 * End of stream has been detected.
 */
export interface EndOfStream {
  type: 'EndOfStream'
}
